// @ts-nocheck
/**
 * Team names for scheduling rows, resolved from the registration.
 *
 * A game references a team only through its event registration. The
 * registration carries the stored name snapshot and the RecruitLook team id,
 * so one query plus one teams-table lookup names every team on a schedule.
 *
 * Resolution order, first non-empty wins:
 *   1. display_name_override
 *   2. extras_summary.team_name
 *   3. public.teams.display_name where teams.id = team_id
 *      or teams.recruitlook_team_id = recruitlook_team_id
 *   4. the caller's fallback ("TBD")
 *
 * Everything is read from RLE's own database, so a staff member's session —
 * or RecruitLook being unreachable — can never turn a named game into TBD.
 */
import { supabase } from "@/integrations/supabase/client";

export async function fetchRegistrationTeamNames(
  registrationIds: (string | null | undefined)[],
  fallback = "TBD"
): Promise<Record<string, string>> {
  const ids = [...new Set(registrationIds.filter(Boolean).map(String))];
  const names: Record<string, string> = {};
  if (ids.length === 0) return names;

  const { data: regs } = await supabase
    .from("event_registrations_v2")
    .select("id, display_name_override, recruitlook_team_id, team_id, extras_summary, division, age_group")
    .in("id", ids);

  // 1 + 2: stored snapshots on the registration itself.
  const teamIds = new Set<string>();
  const rlIds = new Set<string>();
  for (const r of regs || []) {
    const name =
      (r.display_name_override && String(r.display_name_override).trim()) ||
      (r.extras_summary?.team_name && String(r.extras_summary.team_name).trim()) ||
      "";
    if (name) names[r.id] = name;
    if (r.team_id) teamIds.add(String(r.team_id));
    if (r.recruitlook_team_id) rlIds.add(String(r.recruitlook_team_id));
  }

  // 3: teams table, matched by either the local id or the RecruitLook id.
  const stillMissing = (regs || []).filter((r: any) => !names[r.id]);
  if (stillMissing.length) {
    const nameByTeamId: Record<string, string> = {};
    const nameByRlId: Record<string, string> = {};
    if (teamIds.size) {
      const { data: teams } = await supabase
        .from("teams")
        .select("id, display_name")
        .in("id", [...teamIds]);
      for (const t of teams || []) {
        if (t.display_name) nameByTeamId[String(t.id)] = t.display_name;
      }
    }
    if (rlIds.size) {
      const { data: teams } = await supabase
        .from("teams")
        .select("recruitlook_team_id, display_name")
        .in("recruitlook_team_id", [...rlIds]);
      for (const t of teams || []) {
        if (t.display_name && t.recruitlook_team_id) {
          nameByRlId[String(t.recruitlook_team_id)] = t.display_name;
        }
      }
    }
    for (const r of stillMissing) {
      const name =
        (r.team_id && nameByTeamId[String(r.team_id)]) ||
        (r.recruitlook_team_id && nameByRlId[String(r.recruitlook_team_id)]) ||
        "";
      if (name) names[r.id] = name;
    }
  }

  // 4: fallback for anything still unnamed (bracket slots before pool play).
  for (const id of ids) {
    if (!names[id]) names[id] = fallback;
  }
  return names;
}
